import { useEffect, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { useVTTStore } from '@/store'
import { useInitiativeStore } from '@/store/initiativeStore'

/**
 * Player-facing initiative round-start prompt (#161 follow-up 3b).
 *
 * When the GM starts an initiative round, the server broadcasts
 * `initiative_updated` with a non-empty `mapId`; useWebSocket writes it into
 * `initiativeStore` for every client. The GM gets the InitiativePanel controls
 * in the sidebar, but a player has no obvious cue that combat just started and
 * that they need to roll in from their sheet — the only hint lived buried in
 * the sidebar's InitiativePanel empty state.
 *
 * This floating banner (mounted once at the app root, mirroring GMHandoffOffer)
 * appears for players when:
 *   - initiative is started for the *active* map (store mapId === active map id), and
 *   - the player owns a token on that map that hasn't rolled in yet
 *     (no matching entry in the initiative order).
 *
 * It dismisses automatically once the player's token appears in the entries
 * (they rolled), the GM clears initiative, or the active map changes. Clicking
 * "Roll initiative" opens the player's character sheet, where the Init button
 * performs the actual roll-in (the canonical roll path, issue #162) — we don't
 * duplicate the roll emit here. A manual "×" lets a player hide it for the
 * round if they want it out of the way.
 */
export function InitiativePrompt({
  currentUserId,
  isGM,
}: {
  currentUserId?: string
  isGM: boolean
}) {
  const mapId = useInitiativeStore((s) => s.mapId)
  const entries = useInitiativeStore((s) => s.entries)
  const round = useInitiativeStore((s) => s.round)
  const getActiveMap = useVTTStore((s) => s.getActiveMap)
  const getActiveMapTokens = useVTTStore((s) => s.getActiveMapTokens)
  const openCharacterSheet = useVTTStore((s) => s.openCharacterSheet)

  // Per-round manual dismissal. Keyed on round so a new round re-prompts.
  const [dismissedRound, setDismissedRound] = useState<number | null>(null)

  const activeMap = getActiveMap()
  const startedHere = !!activeMap && !!mapId && mapId === activeMap.id

  // Reset the manual dismissal whenever initiative stops or the round advances.
  useEffect(() => {
    if (!startedHere) setDismissedRound(null)
  }, [startedHere])

  // The GM drives initiative from the panel; this prompt is for players only.
  if (isGM || !startedHere || !currentUserId) return null

  // The player's own tokens on the active map.
  const myTokens = getActiveMapTokens().filter(
    (t) => t.ownerUserId === currentUserId,
  )
  if (myTokens.length === 0) return null

  // Already rolled in? (Any of my tokens has an initiative entry.)
  const myTokenIds = new Set(myTokens.map((t) => t.id))
  const alreadyRolled = entries.some((e) => myTokenIds.has(e.tokenId))
  if (alreadyRolled) return null

  if (dismissedRound === round) return null

  // The character whose sheet we open to roll — prefer a token that links a
  // character. If none of my tokens link a character there is nothing to roll.
  const rollableToken = myTokens.find((t) => t.characterId)
  if (!rollableToken?.characterId) return null
  const characterId = rollableToken.characterId

  return (
    <div
      className="ac-card"
      role="alertdialog"
      aria-label="Initiative started"
      style={{
        position: 'fixed',
        top: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 250,
        maxWidth: 380,
        padding: '0.875rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
        borderColor: 'hsl(var(--ac-mint-primary) / 0.5)',
      }}
    >
      <div className="ac-panel-eyebrow">Combat · Round {round}</div>
      <div style={{ fontSize: '0.875rem' }}>
        Initiative has started. Roll initiative to join the turn order.
      </div>
      <div className="ac-help" style={{ fontSize: '0.75rem' }}>
        Click below to open your sheet, then tap <b>Init</b> to roll in.
      </div>
      <div
        style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setDismissedRound(round)}
        >
          Later
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => openCharacterSheet(characterId)}
        >
          Roll initiative
        </Button>
      </div>
    </div>
  )
}
